/**
 * Local persistence for cookie consent. There's no consent-storage backend
 * endpoint yet (only `consentRecordSchema` in libs/validation/src/privacy.ts
 * describing the shape a future one would accept), so this stays entirely
 * client-side for now — see contexts/cookie-consent-context.tsx for the
 * React-facing wrapper.
 *
 * Purposes mirror `consentRecordSchema`'s `purpose` enum exactly so a future
 * "sync consent to the server" pass can reuse this shape unchanged.
 */
export type ConsentPurpose = 'non_essential_cookies' | 'marketing_email' | 'analytics';

export type ConsentPreferences = Record<ConsentPurpose, boolean>;

export interface StoredConsent {
  policyVersion: string;
  decidedAt: string;
  preferences: ConsentPreferences;
}

const STORAGE_KEY = 'workingclock.cookie-consent.v1';
/** Bump this if the cookie policy's substance changes enough to warrant re-asking. */
const POLICY_VERSION = '1';

export const CONSENT_CHANGED_EVENT = 'workingclock:consent-changed';

export const ESSENTIAL_ONLY: ConsentPreferences = {
  non_essential_cookies: false,
  marketing_email: false,
  analytics: false,
};

export const ACCEPT_ALL: ConsentPreferences = {
  non_essential_cookies: true,
  marketing_email: true,
  analytics: true,
};

function isConsentPreferences(value: unknown): value is ConsentPreferences {
  if (!value || typeof value !== 'object') return false;
  const record = value as Record<string, unknown>;
  return (
    typeof record.non_essential_cookies === 'boolean' &&
    typeof record.marketing_email === 'boolean' &&
    typeof record.analytics === 'boolean'
  );
}

export function readConsent(): StoredConsent | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const candidate = parsed as Record<string, unknown>;
    if (!isConsentPreferences(candidate.preferences)) return null;
    if (candidate.policyVersion !== POLICY_VERSION) return null;
    return {
      policyVersion: POLICY_VERSION,
      decidedAt: typeof candidate.decidedAt === 'string' ? candidate.decidedAt : new Date().toISOString(),
      preferences: candidate.preferences,
    };
  } catch {
    return null;
  }
}

export function writeConsent(preferences: ConsentPreferences): StoredConsent {
  const record: StoredConsent = {
    policyVersion: POLICY_VERSION,
    decidedAt: new Date().toISOString(),
    preferences,
  };
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
    } catch {
      // localStorage unavailable (cookies/site data blocked, private mode,
      // quota) — the returned record still applies in-memory for this
      // session; the choice just can't persist across visits.
    }
    window.dispatchEvent(new CustomEvent(CONSENT_CHANGED_EVENT));
  }
  return record;
}
